import { TextInput } from '@/components/atoms';
import { ChevronDownSvg } from '@/components/atoms/icons';
import { Typography } from '@/components/atoms/Typography';
import { useTranslation, useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { getNationalities, type Country } from '@/core/utils/countryCodes';
import React, { useRef, useState } from 'react';
import { FlatList, Modal, TextInput as RNTextInput, StyleSheet, Text, TouchableOpacity, View, ViewStyle } from 'react-native';

// Get nationalities from centralized utility (single source of truth)
const NATIONALITIES = getNationalities();

export interface NationalityDropdownProps {
    selectedNationality?: Country | undefined;
    onNationalitySelect: (nationality: Country) => void;
    disabled?: boolean | undefined;
    label?: string;
    placeholder?: string;
    required?: boolean;
    error?: string;
}

export function NationalityDropdown({
    selectedNationality,
    onNationalitySelect,
    disabled = false,
    label,
    placeholder,
    required = false,
    error
}: NationalityDropdownProps) {
    const colors = useUIColors();
    const { t } = useTranslation();
    const { isRTL } = useI18nContext();
    const [isVisible, setIsVisible] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef<RNTextInput>(null);

    const filteredNationalities = NATIONALITIES.filter(nationality =>
        nationality.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleNationalitySelect = (nationality: Country) => {
        // Blur the search input to close keyboard
        searchInputRef.current?.blur();
        // Select the nationality
        onNationalitySelect(nationality);
        // Close modal and clear search
        setIsVisible(false);
        setSearchQuery('');
    };

    // Dynamic styles based on selection state
    const getInputContainerStyle = (): ViewStyle => {
        const hasError = !!error;
        return {
            flexDirection: isRTL ? 'row-reverse' : 'row',
            alignItems: 'center',
            justifyContent: 'center',
            paddingHorizontal: 16,
            paddingVertical: 12,
            borderWidth: 1,
            borderRadius: 8,
            backgroundColor: selectedNationality ? colors.backgroundLevel2 : '#f2f2f2',
            borderColor: hasError ? colors.outlineError : (selectedNationality ? colors.backgroundLevel4 : 'transparent'),
            height: 48,
            gap: 8,
        };
    };

    const styles = StyleSheet.create({
        container: {
            gap: 8,
        },
        labelContainer: {
            flexDirection: isRTL ? 'row-reverse' : 'row',
            alignItems: 'center',
            justifyContent: isRTL ? 'flex-end' : 'flex-start',
            paddingHorizontal: 8,
            paddingVertical: 0,
        },
        label: {
            fontSize: 14, // Font/Size/XS from Figma
            fontWeight: '500', // Font/weight/Medium from Figma
            color: colors.fontPrimary, // UI Colours/Font/Primary from Figma
            fontFamily: 'Matter',
            minHeight: 14,
        },
        required: {
            color: colors.fontPrimary,
            marginLeft: isRTL ? 0 : 2,
            marginRight: isRTL ? 2 : 0,
        },
        inputContainer: {
            // Base styles - dynamic styles applied via getInputContainerStyle()
        },
        disabledContainer: {
            opacity: 0.6,
        },
        selectedContent: {
            flexDirection: isRTL ? 'row-reverse' : 'row',
            alignItems: 'center',
            flex: 1,
            gap: 8,
        },
        flag: {
            fontSize: 20, // Increased for better visibility
            width: 28, // Increased width for proper flag display
            height: 20, // Increased height to prevent cutting
            textAlign: 'center', // Center the flag emoji
            lineHeight: 20, // Match height for proper alignment
        },
        selectedText: {
            color: colors.fontPrimary,
            fontSize: 16,
            fontWeight: '400',
            fontFamily: 'Matter',
            flex: 1,
            textAlign: isRTL ? 'right' : 'left',
        },
        placeholderText: {
            color: colors.fontTertiary, // Use theme color instead of hardcoded
            fontSize: 16,
            fontWeight: '400',
            fontFamily: 'Matter',
            flex: 1,
            textAlign: isRTL ? 'right' : 'left',
        },
        chevron: {
            width: 19,
            height: 19,
            transform: [{ scaleX: isRTL ? -1 : 1 }],
        },
        modal: {
            flex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            justifyContent: 'center',
            alignItems: 'center',
        },
        modalContent: {
            backgroundColor: colors.backgroundLevel1,
            borderRadius: 12,
            width: '90%',
            maxHeight: '80%',
            paddingVertical: 20,
        },
        modalHeader: {
            paddingHorizontal: 20,
            paddingBottom: 16,
            borderBottomWidth: 1,
            borderBottomColor: colors.outlineInactive,
        },
        modalTitle: {
            color: colors.fontPrimary,
            fontSize: 18,
            fontWeight: '600',
            textAlign: 'center',
        },
        searchContainer: {
            paddingHorizontal: 20,
            paddingVertical: 16,
        },
        nationalityList: {
            maxHeight: 300,
        },
        nationalityItem: {
            flexDirection: isRTL ? 'row-reverse' : 'row',
            alignItems: 'center',
            paddingHorizontal: 20,
            paddingVertical: 14, // Increased padding to prevent flag cutoff
            borderBottomWidth: 1,
            borderBottomColor: colors.outlineInactive,
            minHeight: 56, // Ensure enough height for flag display
        },
        selectedNationalityItem: {
            backgroundColor: colors.backgroundLevel3,
        },
        nationalityFlag: {
            fontSize: 24, // Increased for better visibility
            marginRight: isRTL ? 0 : 12,
            marginLeft: isRTL ? 12 : 0,
            width: 32, // Increased width for proper flag display
            height: 24, // Increased height to prevent cutting
            textAlign: 'center', // Center the flag emoji
            lineHeight: 24, // Match height for proper alignment
            includeFontPadding: false, // Remove Android font padding
        },
        nationalityInfo: {
            flex: 1,
        },
        nationalityName: {
            color: colors.fontPrimary,
            fontSize: 16,
            fontWeight: '400',
            textAlign: isRTL ? 'right' : 'left',
        },
    });

    return (
        <View style={styles.container}>
            {/* Label */}
            {label && (
                <View style={styles.labelContainer}>
                    {isRTL ? (
                        <>
                            {required && <Text style={styles.required}>*</Text>}
                            <Text style={styles.label}>{label}</Text>
                        </>
                    ) : (
                        <>
                            <Text style={styles.label}>{label}</Text>
                            {required && <Text style={styles.required}>*</Text>}
                        </>
                    )}
                </View>
            )}

            {/* Error Message */}
            {error && (
                <Text style={{
                    fontSize: 12,
                    color: colors.fontErrorText,
                    marginTop: 4,
                    textAlign: isRTL ? 'right' : 'left',
                    paddingHorizontal: 8,
                }}>
                    {error}
                </Text>
            )}

            {/* Input Container */}
            <TouchableOpacity
                style={[getInputContainerStyle(), disabled && styles.disabledContainer]}
                onPress={() => !disabled && setIsVisible(true)}
                disabled={disabled}
            >
                {/* Chevron first in RTL, last in LTR */}
                {!disabled && isRTL && (
                    <View style={styles.chevron}>
                        <ChevronDownSvg size={19} color="#333333" />
                    </View>
                )}

                {/* Content area with proper RTL ordering */}
                {isRTL ? (
                    // RTL: Country name first, then flag on the right
                    <View style={styles.selectedContent}>
                        {selectedNationality ? (
                            <>
                                <Text style={styles.selectedText}>
                                    {selectedNationality.name}
                                </Text>
                                <Text style={styles.flag}>
                                    {selectedNationality.flag}
                                </Text>
                            </>
                        ) : (
                            <Text style={styles.placeholderText}>
                                {placeholder}
                            </Text>
                        )}
                    </View>
                ) : (
                    // LTR: Flag first, then country name
                    <View style={styles.selectedContent}>
                        {selectedNationality ? (
                            <>
                                <Text style={styles.flag}>
                                    {selectedNationality.flag}
                                </Text>
                                <Text style={styles.selectedText}>
                                    {selectedNationality.name}
                                </Text>
                            </>
                        ) : (
                            <Text style={styles.placeholderText}>
                                {placeholder}
                            </Text>
                        )}
                    </View>
                )}

                {/* Chevron last in LTR, first in RTL */}
                {!disabled && !isRTL && (
                    <View style={styles.chevron}>
                        <ChevronDownSvg size={19} color="#333333" />
                    </View>
                )}
            </TouchableOpacity>

            {/* Modal */}
            <Modal
                visible={isVisible}
                transparent
                animationType="fade"
                onRequestClose={() => setIsVisible(false)}
            >
                <TouchableOpacity
                    style={styles.modal}
                    activeOpacity={1}
                    onPress={() => setIsVisible(false)}
                >
                    <TouchableOpacity
                        style={styles.modalContent}
                        activeOpacity={1}
                        onPress={() => { }}
                    >
                        <View style={styles.modalHeader}>
                            <Typography variant="headline" style={styles.modalTitle}>
                                {t('registration.selectNationality')}
                            </Typography>
                        </View>

                        <View style={styles.searchContainer}>
                            <TextInput
                                ref={searchInputRef}
                                placeholder={t('registration.searchNationality')}
                                value={searchQuery}
                                onChangeText={setSearchQuery}
                                autoCapitalize="none"
                                returnKeyType="search"
                                variant="search"
                            />
                        </View>

                        <FlatList
                            data={filteredNationalities}
                            keyExtractor={(item) => item.country}
                            style={styles.nationalityList}
                            keyboardShouldPersistTaps="handled" // Allow taps on list items even when keyboard is open
                            renderItem={({ item }) => (
                                <TouchableOpacity
                                    style={[
                                        styles.nationalityItem,
                                        selectedNationality?.country === item.country && styles.selectedNationalityItem
                                    ]}
                                    onPress={() => {
                                        // Blur the search input before selecting to ensure clean state
                                        handleNationalitySelect(item);
                                    }}
                                    activeOpacity={0.7}
                                >
                                    <Text style={styles.nationalityFlag}>
                                        {item.flag}
                                    </Text>
                                    <View style={styles.nationalityInfo}>
                                        <Typography variant="body" style={styles.nationalityName}>
                                            {item.name}
                                        </Typography>
                                    </View>
                                </TouchableOpacity>
                            )}
                        />
                    </TouchableOpacity>
                </TouchableOpacity>
            </Modal>
        </View>
    );
}

export { NATIONALITIES };
export type { Country as NationalityItem };

